/**
 * Koordinaten-Apparel V11 (PROJ-71) — Galerie-Vorschau + Gelato-Stick
 * - Live-Vorschau LINKS auf dem grossen Galeriefoto (Ansage Dominik 07.08.):
 *   koordinaten-engine.js (geteilte Engine, byte-identisch mit shared_frontend/
 *   auf dem Render-Server) zeichnet in eine kalibrierte Stick-Zone, die auf das
 *   Flatlay der aktiven Variante injiziert wird (PROJ-28-Muster, jetzt Engine
 *   statt grober Text-Overlay).
 * - Cart-Add rendert die Stick-Datei (POST /api/v1/blanket/render/, Besties-
 *   Muster) und setzt _Provider=gelato + _job_id + _print_image_url + _Druckfarbe
 * - Ortsname ist kostenlos: der alte Aufschlag-Hijack ist entfernt
 * - Mapbox-Block (in main-product Custom-Liquid) liefert Lat/Lon + Ortsname
 * - Cart-Properties via Hidden-Input-Injection ins main-product Add-to-Cart-Form
 * - Rerender-fest: Theme-Editor (shopify:section:load) und Prestige-Variant-
 *   Rerender ersetzen das Block-DOM — els/Listener werden danach neu gebunden
 *   (vorher hing das Suchfeld nach jedem Rerender tot, Bug 07.08.).
 */
(function () {
  'use strict';

  const mapboxRoot = document.querySelector('[data-ka-mapbox-block]');
  if (!mapboxRoot) return;

  const config = {
    productHandle: mapboxRoot.dataset.productHandle,
    mapboxToken: mapboxRoot.dataset.mapboxToken,
    googleKey: mapboxRoot.dataset.googleKey,
  };

  const state = {
    lat: null,
    lon: null,
    address: '',
    ortsname: '',
    nameToggleOn: false,
    map: null,
    marker: null,
    overlayInjected: false,
    isDarkColor: false,
    /* PROJ-71: Gelato-Token der aktiven Variante (Farbe = Flatlay-Key) */
    colorToken: 'black',
    sizeToken: '',
    /* PROJ-28: Media-ID des Flatlays der aktiven Variante (Ein-Bild-Customizer) */
    currentVariantMediaId: null,
  };

  /* els werden bei jedem Rerender neu aus dem DOM gezogen (V11): der
     Theme-Editor und Prestige ersetzen den Block, einmal gecachte Referenzen
     zeigen danach auf tote Knoten. */
  let els = {};
  function refreshEls() {
    const root = document.querySelector('[data-ka-mapbox-block]') || mapboxRoot;
    els = {
      root: root,
      coordLat: root.querySelector('[data-ka-coord-lat]'),
      coordLon: root.querySelector('[data-ka-coord-lon]'),
      search: root.querySelector('[data-ka-search]'),
      searchClear: root.querySelector('[data-ka-search-clear]'),
      map: root.querySelector('[data-ka-map]'),
      mapPlaceholder: root.querySelector('[data-ka-map-placeholder]'),
      mapContainer: root.querySelector('[data-ka-map-container]'),
      mapHint: root.querySelector('[data-ka-map-hint]'),
      nameToggle: root.querySelector('[data-ka-name-toggle]'),
      nameInput: root.querySelector('[data-ka-name-input]'),
      nameField: root.querySelector('[data-ka-name-field]'),
      notice: root.querySelector('[data-ka-notice]'),
    };
    return els;
  }
  refreshEls();

  /* ========== PROJ-71 GALERIE-VORSCHAU (Stick-Engine auf dem Flatlay) ==========
   * Die Live-Vorschau sitzt LINKS auf dem grossen Galeriefoto: eine kalibrierte
   * Stick-Zone wird auf das Flatlay der aktiven Variante injiziert (PROJ-28-
   * Muster), darin zeichnet die geteilte Engine (koordinaten-engine.js,
   * byte-identisch mit dem Render-Server). Flatlay-Wechsel macht das Theme
   * selbst (Varianten-featured-media). */

  const RENDER_API = 'https://tiles.distantlines.de';

  function garmentTyp() {
    return String(config.productHandle || '').includes('hoodie') ? 'hoodie' : 'tee';
  }
  function garmentBlank() {
    return garmentTyp() === 'hoodie' ? 'gildan-18500' : 'gildan-5000';
  }

  /* Farb-Optionswert (deutsch, option "Farbe") -> Gelato-Farbtoken */
  function colorToken(optionValue) {
    const t = String(optionValue || '').toLowerCase();
    if (t.includes('schwarz') || t.includes('black')) return 'black';
    if (t.includes('pink') || t.includes('rosa')) return 'light-pink';
    if (t.includes('ash') || t.includes('grau')) return 'ash';
    if (t.includes('natural') || t.includes('beige')) return 'natural';
    return 'white';
  }

  /* Optionen robust aufloesen: Position von "Farbe"/"Größe" aus den
     Produkt-Optionen lesen statt option1/option2 zu raten. */
  function variantTokens(v) {
    let farbe = v.option1, groesse = v.option2;
    try {
      const opts = JSON.parse(document.getElementById('ka-options-data').textContent);
      opts.forEach(function (o, i) {
        const n = String(o.name || '').toLowerCase();
        const val = v['option' + (i + 1)];
        if (n.indexOf('farbe') === 0 || n.indexOf('color') === 0) farbe = val;
        if (n.indexOf('gr') === 0 || n.indexOf('size') === 0) groesse = val;
      });
    } catch (err) { /* Fallback: option1 = Farbe, option2 = Groesse */ }
    return { farbe: farbe, groesse: groesse };
  }

  function stickInk() {
    /* Druckfarbe (V12, DTG): schwarzer Stoff bekommt weisse Schrift,
       alle anderen dunkle — gleiche Regel wie zu Stick-Zeiten. */
    return state.colorToken === 'black' ? '#ffffff' : '#1f1d1a';
  }

  function renderStage() {
    if (typeof window.renderKoordinatenDesign !== 'function') return;
    const mount = document.querySelector('[data-koord-mount]');
    if (!mount) return; // Zone noch nicht injiziert — injectStickereiOverlay holt das nach
    const coords = (state.lat != null && state.lon != null)
      ? `${decimalToFormatted(state.lat, true)}, ${decimalToFormatted(state.lon, false)}`
      : '52.27° N, 10.52° E';
    const ortsname = state.nameToggleOn && state.ortsname.trim()
      ? state.ortsname.trim().toUpperCase() : null;
    window.renderKoordinatenDesign(mount, { coords: coords, ortsname: ortsname, ink: stickInk() });
  }

  /* ========== CUSTOMIZER-OVERLAY auf die THEME-GALERIE injizieren ==========
   * PROJ-28 (Ein-Bild-Customizer, aintforever-Muster): Overlay + Badge sitzen
   * auf GENAU EINEM Slide - dem Flatlay der AKTIVEN Variante. Alle anderen
   * Slides (andere Farb-Flatlays, Lifestyle-Fotos) bleiben statisch. Beim
   * Variantenwechsel ZIEHT das Overlay auf das neue Flatlay um. */

  function activeVariantSlide() {
    // Ziel-Slide: das Medium der aktiven Variante (featured_media),
    // Fallback: erstes Varianten-Flatlay. NIE Lifestyle-Bilder.
    if (state.currentVariantMediaId) {
      const exact = document.querySelector(
        '.product-gallery__media[data-media-id="' + state.currentVariantMediaId + '"]'
      );
      if (exact) return exact;
    }
    return document.querySelector('.product-gallery__media[data-variant-media="true"]');
  }

  function buildOverlayEls() {
    /* V11: statt zweier grob positionierter Textzeilen eine kalibrierte
       Stick-Zone, in die die geteilte Engine zeichnet. Zone-Position/-Groesse
       kommen aus koordinaten-apparel.css (.ka-motiv-zone--tee/--hoodie,
       vermessen gegen die 1600x1900-Flatlays). */
    const overlay = document.createElement('div');
    overlay.className = 'ka-motiv-zone ka-motiv-zone--' + garmentTyp();
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = '<div class="ka-motiv-zone__mount" data-koord-mount></div>';
    // Kein "Personalisierbar"-Badge mehr (Ansage Dominik 08.08.)
    return { overlay };
  }

  let overlayEls = null; // {overlay, badge} — lebt ausserhalb von els (Rerender)

  function injectStickereiOverlay() {
    const slide = activeVariantSlide();
    if (!slide) {
      // Galerie noch nicht da, später nochmal
      setTimeout(injectStickereiOverlay, 250);
      return;
    }

    // Elemente existieren evtl. schon (Variantenwechsel) → umziehen;
    // nach Theme-Rerender sind sie aus dem DOM geflogen → neu bauen.
    if (!overlayEls || !document.body.contains(overlayEls.overlay)) {
      overlayEls = buildOverlayEls();
    }
    if (overlayEls.overlay.parentElement !== slide) {
      // Alten Traeger freigeben, sonst blieben im fixierten Modus nach
      // einem Variantenwechsel ZWEI Kacheln sichtbar.
      const altSlide = overlayEls.overlay.parentElement;
      if (altSlide && altSlide.classList) altSlide.classList.remove('ka-slide');
      const computed = window.getComputedStyle(slide);
      if (computed.position === 'static') slide.style.position = 'relative';
      slide.appendChild(overlayEls.overlay);
    }
    slide.classList.add('ka-slide');
    state.overlayInjected = true;
    // Nach Theme-Rerender geht die Fixierung mit dem alten DOM verloren —
    // hier wieder anlegen, sofern schon personalisiert wurde.
    if (state.lat != null && state.lon != null) fixiereGalerie(slide);
    updateStickereiOverlayText();
  }

  /* FamWalls-Muster (Ansage Dominik 10.08.): Sobald ein Ort gewaehlt ist,
     zeigt die Galerie NUR noch die Kachel mit der Live-Vorschau — jeder
     Bildwechsel wuerde die Konfiguration verstecken. Vorher (nur
     Platzhalter-Koordinaten) bleibt die Galerie frei browsbar. */
  function fixiereGalerie(slideVielleicht) {
    const slide = slideVielleicht || (overlayEls && overlayEls.overlay.parentElement);
    if (!slide) return;
    slide.classList.add('ka-slide');
    const root = slide.closest('.product-gallery');
    if (root) root.classList.add('ka-galerie-fixiert');
    const scroller = slide.closest('.product-gallery__carousel');
    if (scroller) scroller.scrollLeft = 0;
  }

  function updateStickereiOverlayText() {
    /* V11: Die Engine ist die einzige Darstellung — zentrale Update-Stelle. */
    renderStage();
  }

  /* ========== UTILITIES ========== */

  /* V9.23: Decimal-Format `47.95° N` — 2 Nachkommastellen, Letter NACH Wert */
  function decimalToFormatted(value, isLat) {
    const absVal = Math.abs(value);
    const letter = isLat ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
    return `${absVal.toFixed(2)}° ${letter}`;
  }

  /* V9.24 + V9.79: Auto-Color-Adjust für dunkle Hoodies.
     State.isDarkColor wird persistiert, sodass neue Overlays nach partial-rerender
     beim Erstellen direkt die Klasse bekommen (siehe injectStickereiOverlay). */
  function applyDarkColorAdjust(variantTitle) {
    if (!variantTitle) {
      state.isDarkColor = false;
    } else {
      const darkColors = ['schwarz', 'black', 'navy', 'dark', 'anthrazit'];
      const titleLower = String(variantTitle).toLowerCase();
      state.isDarkColor = darkColors.some(c => titleLower.includes(c));
    }
  }
  document.addEventListener('variant:change', function(e) {
    const v = e.detail && e.detail.variant;
    if (!v) return;
    applyDarkColorAdjust(v.option1 || v.title || '');
    /* PROJ-71: Gelato-Token der neuen Variante uebernehmen */
    const tokens = variantTokens(v);
    state.colorToken = colorToken(tokens.farbe || v.title || '');
    state.sizeToken = String(tokens.groesse || '').toLowerCase();
    renderStage();
    /* PROJ-28: Overlay zieht auf das Flatlay der neuen Variante um */
    if (v.featured_media && v.featured_media.id) {
      state.currentVariantMediaId = String(v.featured_media.id);
    }
    injectStickereiOverlay();
  });

  /* V9.79 Initial-Apply: Default-Variant aus <script data-variant> JSON lesen
     und Dark-Class beim Page-Load setzen — sonst bleibt schwarzer Default-Hoodie
     mit schwarzer Schrift bis zum ersten Variant-Wechsel. */
  function applyInitialDarkColorAdjust() {
    const scripts = document.querySelectorAll('script[data-variant]');
    for (const s of scripts) {
      try {
        const v = JSON.parse(s.textContent);
        if (v && (v.option1 || v.title)) {
          applyDarkColorAdjust(v.option1 || v.title);
          /* PROJ-71: Token der Default-Variante fuer Stage + Render-Payload */
          const tokens = variantTokens(v);
          state.colorToken = colorToken(tokens.farbe || v.title || '');
          state.sizeToken = String(tokens.groesse || '').toLowerCase();
          renderStage();
          /* PROJ-28: initiales Ziel-Flatlay der Default-Variante merken */
          if (v.featured_media && v.featured_media.id) {
            state.currentVariantMediaId = String(v.featured_media.id);
            injectStickereiOverlay();
          }
          return;
        }
      } catch (err) { /* ignore */ }
    }
  }

  /* ========== HIDDEN-INPUT-INJECTION ins main-product Form ========== */

  function findProductForm() {
    return document.querySelector('product-form form[action*="/cart/add"]')
        || document.querySelector('form[action*="/cart/add"]');
  }

  function ensureProperty(name, value) {
    const form = findProductForm();
    if (!form) return;
    const inputName = `properties[${name}]`;
    let input = form.querySelector(`input[name="${inputName}"][data-ka-injected]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      input.dataset.kaInjected = 'true';
      form.appendChild(input);
    }
    input.value = value || '';
  }

  function syncAllProperties() {
    if (state.lat != null && state.lon != null) {
      ensureProperty('_lat', state.lat.toFixed(6));
      ensureProperty('_lon', state.lon.toFixed(6));
      ensureProperty('_Koordinaten', `${decimalToFormatted(state.lat, true)}, ${decimalToFormatted(state.lon, false)}`);
    } else {
      ensureProperty('_lat', '');
      ensureProperty('_lon', '');
      ensureProperty('_Koordinaten', '');
    }
    ensureProperty('_Adresse', state.address || '');
    ensureProperty('_Ortsname',
      state.nameToggleOn && state.ortsname.trim()
        ? state.ortsname.trim().toUpperCase()
        : ''
    );
  }

  /* ========== COORDS UPDATE ========== */

  function updateCoordsDisplay() {
    if (state.lat == null || state.lon == null) return;
    /* V9.23: Decimal-Format */
    const latStr = decimalToFormatted(state.lat, true);
    const lonStr = decimalToFormatted(state.lon, false);
    if (els.coordLat) els.coordLat.textContent = latStr;
    if (els.coordLon) els.coordLon.textContent = lonStr;
    updateStickereiOverlayText();
    fixiereGalerie();
    syncAllProperties();
  }

  function updateOrtsnameDisplay() {
    updateStickereiOverlayText();
    syncAllProperties();
  }

  /* ========== MAP ========== */

  function initMap() {
    if (state.map) return;
    if (typeof mapboxgl === 'undefined') {
      setTimeout(initMap, 250);
      return;
    }
    mapboxgl.accessToken = config.mapboxToken;
    state.map = new mapboxgl.Map({
      container: els.mapContainer,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [10.4515, 51.1657],
      zoom: 5,
    });
    state.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    state.marker = new mapboxgl.Marker({ draggable: true, color: '#1a1a1a' });
    state.map.on('load', () => { if (els.mapPlaceholder) els.mapPlaceholder.style.display = 'none'; });
    state.map.on('click', (e) => {
      placeMarker(e.lngLat.lng, e.lngLat.lat);
      reverseGeocode(e.lngLat.lng, e.lngLat.lat);
    });
    state.marker.on('dragend', () => {
      const lngLat = state.marker.getLngLat();
      state.lat = lngLat.lat;
      state.lon = lngLat.lng;
      updateCoordsDisplay();
      reverseGeocode(lngLat.lng, lngLat.lat);
    });
  }

  function placeMarker(lng, lat) {
    state.lat = lat;
    state.lon = lng;
    state.marker.setLngLat([lng, lat]).addTo(state.map);
    state.map.flyTo({ center: [lng, lat], zoom: 14, speed: 1.4 });
    if (els.mapHint) els.mapHint.hidden = false;
    if (els.notice) els.notice.hidden = true;
    updateCoordsDisplay();
  }

  /* ========== GOOGLE PLACES ========== */

  let autocomplete = null;
  function initAutocomplete() {
    if (autocomplete) return;
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      setTimeout(initAutocomplete, 250);
      return;
    }
    if (!els.search) return;
    autocomplete = new google.maps.places.Autocomplete(els.search, {
      fields: ['geometry', 'formatted_address', 'name'],
      types: ['geocode'],
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place || !place.geometry) return;
      const lng = place.geometry.location.lng();
      const lat = place.geometry.location.lat();
      state.address = place.formatted_address || place.name || '';

      if (!state.map) initMap();
      /* V11-Bugfix: 'load' feuert bei Mapbox genau EINMAL. Wer danach
         once('load') anhaengt (weil loaded() mitten im Tile-Nachladen false
         ist), wartet ewig — Ortswahl kam an, aber Marker/Koordinaten/Engine
         passierten nie ("Eingabe tut nichts", 07.08.). isStyleLoaded deckt
         den fertig-geladenen Fall ab, 'idle' feuert bei jedem Render-Settle. */
      const tryPlace = () => {
        if (!state.map) { setTimeout(tryPlace, 200); return; }
        if (state.map.loaded() || state.map.isStyleLoaded()) { placeMarker(lng, lat); return; }
        state.map.once('idle', () => placeMarker(lng, lat));
      };
      tryPlace();
    });
  }

  function reverseGeocode(lng, lat) {
    if (typeof google === 'undefined' || !google.maps) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        state.address = results[0].formatted_address;
        if (els.search) els.search.value = state.address;
        if (els.searchClear) els.searchClear.hidden = false;
        syncAllProperties();
      }
    });
  }

  /* ========== ORTSNAME TOGGLE ========== */

  function setupNameToggle() {
    // kaBound-Guard: wird nach jedem Rerender erneut aufgerufen — neue Knoten
    // binden, bestehende nicht doppelt.
    if (els.nameToggle && !els.nameToggle.dataset.kaBound) {
      els.nameToggle.dataset.kaBound = '1';
      els.nameToggle.addEventListener('change', (e) => {
        state.nameToggleOn = e.target.checked;
        if (els.nameInput) els.nameInput.hidden = !state.nameToggleOn;
        updateOrtsnameDisplay();
      });
    }
    if (els.nameField && !els.nameField.dataset.kaBound) {
      els.nameField.dataset.kaBound = '1';
      els.nameField.addEventListener('input', (e) => {
        state.ortsname = e.target.value;
        updateOrtsnameDisplay();
      });
    }
  }

  /* ========== ADD-TO-CART: STICK-DATEI RENDERN (PROJ-71) ==========
   * Besties-Muster (besties-shirt.js vorbereitenUndSenden): Submit abfangen,
   * Render-POST an den Server, Gelato-Properties ins SUBMITTENDE Formular,
   * dann nativ requestSubmit() — so laeuft der Warenkorb-Ablauf des Themes
   * (Drawer, Zaehler, Tracking) unveraendert weiter. Der alte Ortsname-
   * Aufschlag-Hijack ist entfernt: Ortsname ist kostenlos. */

  function setPropIn(form, name, value) {
    const inputName = `properties[${name}]`;
    let input = form.querySelector(`input[name="${inputName}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      input.dataset.kaInjected = 'true';
      form.appendChild(input);
    }
    input.value = value || '';
  }

  function setupCartHijack() {
    let vorbereitet = false; // true = Props sitzen, dieser Submit darf nativ durch

    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      if (state.lat == null || state.lon == null) {
        e.preventDefault();
        if (els.notice) {
          els.notice.hidden = false;
          els.notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      if (vorbereitet) { vorbereitet = false; return; }

      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      const jobId = (window.crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'ka-' + Date.now() + '-' + Math.floor(Math.random() * 1e9);
      const coordsStr = `${decimalToFormatted(state.lat, true)}, ${decimalToFormatted(state.lon, false)}`;
      const ortsname = state.nameToggleOn && state.ortsname.trim()
        ? state.ortsname.trim().toUpperCase() : null;
      const idInput = form.querySelector('[name="id"]');

      // Fallback VORAB (Besties-Muster): der Server kann den Stick spaeter aus
      // den gespeicherten Job-Daten rekonstruieren, falls der Render-POST hier
      // zwar ankam, aber die Antwort verloren ging.
      let printImageUrl = RENDER_API + '/api/v1/blanket/data/' + jobId + '/';

      // Sofort-Vorschau fuer den Warenkorb: Schnappschuss des Galerie-Slides
      // (Flatlay + Motiv) in sessionStorage — der Drawer zeigt ihn, ohne auf
      // den Server-Render zu warten. Fail-soft.
      let schnappschuss = Promise.resolve();
      try {
        const slide = activeVariantSlide();
        if (slide && typeof window.html2canvas === 'function') {
          schnappschuss = window.html2canvas(slide, {
            useCORS: true,
            backgroundColor: '#f6f6f6',
            scale: Math.min(1, 360 / Math.max(1, slide.clientWidth)),
            logging: false,
          }).then(c => {
            sessionStorage.setItem('kaCartPreview:' + jobId, c.toDataURL('image/jpeg', 0.75));
          }).catch(() => {});
        }
      } catch (err) { /* non-fatal */ }

      try {
        const resp = await fetch(RENDER_API + '/api/v1/blanket/render/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id: jobId,
            product: 'koordinaten',
            garment: {
              blank: garmentBlank(),
              color: state.colorToken || 'black',
              size: state.sizeToken || '',
            },
            coords: coordsStr,
            ortsname: ortsname,
            ink: stickInk(),
            variant_id: idInput ? idInput.value : null,
            // V12: DTG-Frontdruck — exakte Gelato-Druckflaeche (kein fit-Scaling)
            print: { width: 30.5, height: 40.6, dpi: 300 },
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok && resp.status !== 202) {
          // Ein 400 heisst: Server hat die Kombination abgelehnt (z. B. Tee in
          // einer Farbe ohne Gelato-Stick-UID) — das darf NICHT in den Warenkorb.
          throw new Error((data && (data.error || data.detail)) || ('HTTP ' + resp.status));
        }
        if (data && data.print_image_url) printImageUrl = data.print_image_url;

        // Standard-Props sitzen via ensureProperty nur im ERSTEN Form der Seite —
        // hier ins tatsaechlich submittende Formular spiegeln (Sticky-Bar-Fall).
        setPropIn(form, '_lat', state.lat.toFixed(6));
        setPropIn(form, '_lon', state.lon.toFixed(6));
        setPropIn(form, '_Koordinaten', coordsStr);
        setPropIn(form, '_Adresse', state.address || '');
        setPropIn(form, '_Ortsname', ortsname || '');
        setPropIn(form, '_Provider', 'gelato');
        setPropIn(form, '_job_id', jobId);
        setPropIn(form, '_print_image_url', printImageUrl);
        // Cart-Vorschau (PROJ-72): Flatlay-Komposit vom Render-Worker
        setPropIn(form, '_preview_image_url', RENDER_API + '/media/blanket_renders/' + jobId + '_cartpreview.jpg');
        setPropIn(form, '_Druckfarbe', state.colorToken === 'black' ? 'Weiß' : 'Schwarz');

        vorbereitet = true;
        await schnappschuss;
        if (submitBtn) submitBtn.disabled = false;
        if (form.requestSubmit) form.requestSubmit(); else form.submit();
        // Direkt wieder entwerten: ein zweiter Klick rendert einen NEUEN Job,
        // statt dieselbe job_id doppelt in den Warenkorb zu legen.
        vorbereitet = false;
      } catch (err) {
        if (submitBtn) submitBtn.disabled = false;
        console.error('[KA] Stick-Render fehlgeschlagen', err);
        if (els.notice) {
          els.notice.textContent = 'Deine Personalisierung konnte nicht vorbereitet werden ('
            + ((err && err.message) || 'Verbindungsfehler')
            + '). Bitte versuche es noch einmal.';
          els.notice.hidden = false;
          els.notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  /* ========== LAZY LOAD MAP ========== */

  function setupLazyMap() {
    if (!els.map || els.map.dataset.kaBound) return;
    els.map.dataset.kaBound = '1';
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          initMap();
          observer.disconnect();
        }
      });
    }, { rootMargin: '200px' });
    observer.observe(els.map);
    if (els.search) els.search.addEventListener('focus', initMap, { once: true });
  }

  /* ========== SEARCH CLEAR ========== */

  function setupSearchClear() {
    if (!els.search || !els.searchClear || els.searchClear.dataset.kaBound) return;
    els.searchClear.dataset.kaBound = '1';
    els.search.addEventListener('input', () => { els.searchClear.hidden = !els.search.value; });
    els.searchClear.addEventListener('click', () => {
      els.search.value = '';
      els.searchClear.hidden = true;
    });
  }

  /* ========== COLOR-SWATCH BACKGROUND-IMAGE FORCEN ==========
     Prestige rendert Color-Swatches manchmal als Color-Hex statt als Variant-Bild.
     Hier setzen wir per JS die --swatch-background CSS-Variable auf das
     Variant-Featured-Image (oder Produkt-Featured-Image als Fallback). */
  function forceSwatchImages() {
    // Variant-Daten aus Inline-JSON lesen (funktioniert auch bei Draft-Produkten)
    const variantsScript = document.getElementById('ka-variants-data');
    if (!variantsScript) return;

    let variants;
    try { variants = JSON.parse(variantsScript.textContent); } catch { return; }
    if (!Array.isArray(variants)) return;

    // Für jeden einzigartigen Color-Wert das erste Variant-Featured-Image finden
    const colorImages = {};
    variants.forEach(v => {
      const color = v.option1;
      if (color && !colorImages[color] && v.featured_image && v.featured_image.src) {
        colorImages[color] = v.featured_image.src;
      }
    });

    // Color-Swatches im DOM finden und Background setzen
    document.querySelectorAll('.color-swatch').forEach(swatch => {
      const label = swatch.getAttribute('aria-label')
        || swatch.previousElementSibling?.value
        || swatch.dataset.value;
      if (!label) return;
      const imgUrl = colorImages[label];
      if (imgUrl) {
        swatch.style.setProperty('--swatch-background', `url("${imgUrl}")`);
      }
    });
  }

  /* ========== RE-RENDER HANDLING ==========
     Prestige's product-rerender (Variant-Change) und der Theme-Editor
     (shopify:section:load) ersetzen den Block im DOM. Danach: els neu ziehen,
     Feld-Listener + Google-Autocomplete NEU binden (V11 — vorher blieb das
     Autocomplete am toten alten Suchfeld haengen: "Eingabe tut nichts"),
     Map-Canvas umziehen, Overlay neu anheften. */
  function reattachAfterRerender() {
    const alterSearch = els.search;
    refreshEls();

    // Map-Canvas in den neuen Container moven
    if (state.map && els.mapContainer && state.map.getContainer() !== els.mapContainer) {
      const oldContainer = state.map.getContainer();
      while (oldContainer.firstChild) {
        els.mapContainer.appendChild(oldContainer.firstChild);
      }
      // Mapbox-Map-Container-Referenz updaten ist intern — als Workaround: resize triggert reflow
      try { state.map.resize(); } catch (e) {}
    }

    // Suchfeld wurde ersetzt → Autocomplete + Feld-Listener neu binden,
    // eingetippten/aufgeloesten Ort zuruecksetzen ins neue Feld.
    if (els.search && els.search !== alterSearch) {
      autocomplete = null;
      initAutocomplete();
      if (state.address) {
        els.search.value = state.address;
        if (els.searchClear) els.searchClear.hidden = false;
      }
    }
    setupNameToggle();
    setupSearchClear();
    setupLazyMap();
    if (els.nameToggle) els.nameToggle.checked = state.nameToggleOn;
    if (els.nameInput) els.nameInput.hidden = !state.nameToggleOn;
    if (els.nameField) els.nameField.value = state.ortsname;
    updateCoordsDisplay();

    // Inputs neu finden + Properties re-injecten
    syncAllProperties();
    forceSwatchImages();
    /* PROJ-28: nach Theme-Rerender ist das Overlay ggf. mit dem alten DOM
       verschwunden - auf dem aktiven Varianten-Flatlay neu anheften. */
    injectStickereiOverlay();
  }

  function setupRerenderHook() {
    // Hook auf Prestige's product-rerender Custom-Element
    document.addEventListener('variant:change', () => {
      // Re-Render passiert nach dem Event — Timeout damit DOM aktualisiert ist
      setTimeout(reattachAfterRerender, 50);
      setTimeout(forceSwatchImages, 150);
    });
    // Backup: bei jedem product-form Change auch reattach
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="id"], select[name="id"]')) {
        setTimeout(reattachAfterRerender, 100);
        setTimeout(forceSwatchImages, 250);
      }
    });
    // Theme-Editor rerendert Sections beim Laden und bei jeder Einstellung —
    // genau der Fall, in dem das Suchfeld bisher tot war (Bug 07.08.).
    document.addEventListener('shopify:section:load', () => {
      setTimeout(reattachAfterRerender, 50);
    });
  }

  /* ========== INIT ========== */

  function init() {
    setupNameToggle();
    setupSearchClear();
    setupLazyMap();
    setupCartHijack();
    setupRerenderHook();
    initAutocomplete();
    injectStickereiOverlay();
    applyInitialDarkColorAdjust();
    renderStage(); /* PROJ-71: Stage auch ohne data-variant-Script initial fuellen */
    // forceSwatchImages mit retry — Prestige rendert Swatches ggf. erst nach init()
    forceSwatchImages();
    setTimeout(forceSwatchImages, 300);
    setTimeout(forceSwatchImages, 1000);
    syncAllProperties();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ============================================================
 * V9.1 — Mengenrabatt-Tier-Picker (Pill-Tiles)
 * Click auf Tile setzt:
 *  - versteckte Radio + quantity input
 *  - Speech-Bubble (Stück-Preis) sichtbar bei Tier 2/3, hidden bei Tier 1
 *  - Savings-Text "🤤 Du sparst X €" (active bei Tier 2/3)
 *  - Progress-Bar Tier-Attribut (0/50/100%)
 *  - Quantity-Label "Menge: X"
 * ============================================================ */
(function () {
  'use strict';

  function formatMoney(cents) {
    const eur = (cents / 100).toFixed(2).replace('.', ',');
    return eur + ' €';
  }

  function applyTier(root, qty, discount, basePriceCents) {
    const tiles = root.querySelectorAll('.ka-mengenrabatt__tile');
    const tileByQty = {};
    tiles.forEach((t) => {
      const tQty = parseInt(t.dataset.qty, 10);
      tileByQty[tQty] = t;
      t.classList.toggle('is-selected', tQty === qty);
    });

    const radio = root.querySelector(`input[type="radio"][value="${qty}"]`);
    if (radio) radio.checked = true;

    // Quantity-Label
    const qtyLabel = root.querySelector('[data-ka-qty-label]');
    if (qtyLabel) qtyLabel.textContent = qty;

    // Sync quantity into main product-form
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      let qtyInput = form.querySelector('input[name="quantity"]');
      if (!qtyInput) {
        qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = 'quantity';
        form.appendChild(qtyInput);
      }
      qtyInput.value = qty;
    }

    // Speech-Bubble Stück-Preis — V9.26 Clone-Strategy: ALLE Bubbles (Original + Clone) parallel updaten
    const bubbles = document.querySelectorAll('[data-ka-price-bubble]');
    const unitPriceCents = discount > 0
      ? Math.round(basePriceCents * (100 - discount) / 100)
      : basePriceCents;
    bubbles.forEach((bubble) => {
      const textTemplate = bubble.dataset.textTemplate || 'nur {amount} / Stück';
      const showOnlyDiscount = bubble.dataset.showOnlyWithDiscount !== 'false';
      const tipNode = bubble.querySelector('.ka-price-bubble__tip');
      bubble.textContent = textTemplate.replace('{amount}', formatMoney(unitPriceCents));
      if (tipNode) bubble.appendChild(tipNode);
      if (showOnlyDiscount && discount <= 0) {
        bubble.setAttribute('hidden', '');
      } else {
        bubble.removeAttribute('hidden');
      }
    });

    // V9.18 + V9.26: Discount-Badge + Strikethrough in price-block + Bubble-Clone in price-list
    // Snocks-Logik: Sale + Streichpreis = GESAMTPREIS für gewählte Menge.
    //   Tier 2 (2 Stück, 15%): Sale = 2 × 89 × 0.85 = 151,30 €, Streich = 2 × 89 = 178,00 €
    //   Tier 3 (3 Stück, 25%): Sale = 3 × 89 × 0.75 = 200,25 €, Streich = 3 × 89 = 267,00 €
    //   Bubble = Stückpreis MIT Rabatt (basePriceCents × (1-discount/100))
    const priceBlock = document.querySelector('.product-info__block-item[data-block-type="price"]');
    if (priceBlock) {
      // Bestehende Injects entfernen (für Tier-Wechsel)
      priceBlock.querySelectorAll('.ka-price-discount-badge, .ka-price-strikethrough, [data-bubble-cloned]')
        .forEach((el) => el.remove());

      // Sale-Preis-Element finden: Prestige rendert <sale-price>. Bei Re-Render gehen unsere
      // overrides verloren, deshalb merken wir uns den Original-Wert auf dem Element.
      const priceEl = priceBlock.querySelector('sale-price');
      if (priceEl && !priceEl.dataset.kaOriginalHtml) {
        priceEl.dataset.kaOriginalHtml = priceEl.innerHTML;
      }

      if (discount > 0 && priceEl) {
        const salePriceCents = Math.round(basePriceCents * qty * (100 - discount) / 100);
        const strikePriceCents = basePriceCents * qty;

        // Sichtbaren Preis im sale-price überschreiben, sr-only-Label behalten
        const srOnly = priceEl.querySelector('.sr-only');
        const srOnlyHtml = srOnly ? srOnly.outerHTML : '';
        priceEl.innerHTML = srOnlyHtml + formatMoney(salePriceCents);

        // V9.18 Discount-Badge VOR sale-price
        const badge = document.createElement('span');
        badge.className = 'ka-price-discount-badge';
        badge.textContent = '−' + discount + '%';
        priceEl.parentNode.insertBefore(badge, priceEl);

        // V9.26 Bubble-Clone direkt nach sale-price (in gleicher Zeile via flex)
        const originalBubble = document.querySelector('[data-ka-price-bubble]:not([data-bubble-cloned])');
        const priceList = priceBlock.querySelector('price-list, .price-list') || priceEl.parentNode;
        if (originalBubble) {
          const clone = originalBubble.cloneNode(true);
          clone.setAttribute('data-bubble-cloned', 'true');
          clone.removeAttribute('hidden');
          priceEl.parentNode.insertBefore(clone, priceEl.nextSibling);
          // Original-Block-Slot verstecken via CSS-Hook
          const originalWrapper = originalBubble.closest('.ka-price-bubble-wrapper');
          if (originalWrapper) originalWrapper.setAttribute('data-bubble-cloned', 'true');
        }

        // V9.18 Strikethrough NACH allem (eigene Zeile via flex-basis 100%)
        const strike = document.createElement('span');
        strike.className = 'ka-price-strikethrough';
        strike.textContent = 'Statt: ' + formatMoney(strikePriceCents);
        priceList.appendChild(strike);

        // Flex-Layout-Hook auf price-list
        priceList.setAttribute('data-ka-price-row', 'true');
      } else if (priceEl) {
        // Tier 1: Sale-Preis-Original restaurieren
        if (priceEl.dataset.kaOriginalHtml) {
          priceEl.innerHTML = priceEl.dataset.kaOriginalHtml;
        }
        const priceList = priceBlock.querySelector('price-list, .price-list');
        if (priceList) priceList.removeAttribute('data-ka-price-row');
        // Original-Bubble wieder zeigen (Cleanup)
        document.querySelectorAll('.ka-price-bubble-wrapper[data-bubble-cloned]')
          .forEach((el) => el.removeAttribute('data-bubble-cloned'));
      }
    }

    // Hint-Format-Strings aus Section-Attributen (mit {amount}-Placeholder), Fallback auf Defaults
    const hintActiveFormat = root.dataset.hintActive || '🔥 du sparst {amount}';
    const hintInactiveFormat = root.dataset.hintInactive || 'Spare {amount}';

    // Hints pro Tier: active wenn tier === qty, inactive wenn tier > qty, leer wenn tier < qty
    root.querySelectorAll('[data-ka-hint]').forEach((hint) => {
      const tier = parseInt(hint.dataset.kaHint, 10);
      const tile = tileByQty[tier];
      const tierDiscount = tile ? parseInt(tile.dataset.discount, 10) || 0 : 0;
      const totalSaved = Math.round(basePriceCents * tier * tierDiscount / 100);
      const amountStr = formatMoney(totalSaved);
      hint.classList.remove('is-active');
      if (tier < qty) {
        hint.textContent = '';
      } else if (tier === qty && tierDiscount > 0) {
        hint.textContent = hintActiveFormat.replace('{amount}', amountStr);
        hint.classList.add('is-active');
      } else if (tier > qty && tierDiscount > 0) {
        hint.textContent = hintInactiveFormat.replace('{amount}', amountStr);
      } else {
        hint.textContent = '';
      }
    });

    // Position-Marker auf der Progress-Bar: aktiv (grün, nach oben) bei tier === qty,
    // inactive (grau, nach unten) bei tier > qty, hidden bei tier < qty
    root.querySelectorAll('[data-ka-marker]').forEach((marker) => {
      const tier = parseInt(marker.dataset.kaMarker, 10);
      if (tier === qty) marker.setAttribute('data-state', 'active');
      else if (tier > qty) marker.setAttribute('data-state', 'inactive');
      else marker.setAttribute('data-state', 'hidden');
    });

    // Progress-Bar Tier-Attribut + V9.97.3 dynamische Width-Berechnung
    // (vorher CSS-hardcoded auf [data-tier="1..3"] = 33/66/100% — Tier 4/5 Bug).
    const barFill = root.querySelector('[data-ka-bar-fill]');
    if (barFill) {
      barFill.setAttribute('data-tier', String(qty));
      // Anzahl aktiver Tiles (= Tiers mit quantity > 0, im Snippet bereits gefiltert)
      const tiles = root.querySelectorAll('.ka-mengenrabatt__tile');
      const totalTiers = tiles.length;
      // Index des selected Tiles innerhalb der gerenderten Tile-Liste (1-basiert)
      let selectedIndex = 1;
      tiles.forEach((tile, i) => {
        if (parseInt(tile.dataset.qty, 10) === qty) selectedIndex = i + 1;
      });
      const widthPct = totalTiers > 0 ? (selectedIndex / totalTiers) * 100 : 100;
      barFill.style.width = widthPct + '%';
    }
  }

  function init() {
    const root = document.querySelector('[data-ka-mengenrabatt]');
    if (!root) return;

    const tiles = root.querySelectorAll('.ka-mengenrabatt__tile');
    if (!tiles.length) return;

    // V9.81: getBasePrice() liest LIVE aus dem dataset, damit variant:change-Updates
    // (Variant-Preis-Sync) auch in geclosure'd Click-Handlern wirken.
    function getBasePrice() {
      return parseInt(root.dataset.basePrice, 10) || 0;
    }

    tiles.forEach((tile) => {
      tile.addEventListener('click', () => {
        const qty = parseInt(tile.dataset.qty, 10) || 1;
        const discount = parseInt(tile.dataset.discount, 10) || 0;
        applyTier(root, qty, discount, getBasePrice());
      });
    });

    // Initial: Tier 1 (default selected)
    const initialTile = root.querySelector('.ka-mengenrabatt__tile.is-selected') || tiles[0];
    const initialQty = parseInt(initialTile.dataset.qty, 10) || 1;
    const initialDiscount = parseInt(initialTile.dataset.discount, 10) || 0;
    applyTier(root, initialQty, initialDiscount, getBasePrice());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* V9.81: Variant-Preis-Sync bei Wechsel.
     Memory project_apparel_session_2026_05_12 Z639-643: Theme dispatched 'variant:change'
     (KEIN 'on:'-Prefix). V9.20.1 hatte das schon mal gefixt, V9.73-Sync hat's reverted.
     Statt init() neu zu callen (würde Listener doppelt anhängen): nur basePrice updaten
     und aktuelle Tier-Auswahl re-applyen mit neuem Preis. */
  document.addEventListener('variant:change', (e) => {
    const root = document.querySelector('[data-ka-mengenrabatt]');
    if (!root) return;
    const v = e.detail && e.detail.variant;
    if (!v || v.price == null) return;
    root.dataset.basePrice = String(v.price);
    const selectedTile = root.querySelector('.ka-mengenrabatt__tile.is-selected')
                      || root.querySelector('.ka-mengenrabatt__tile');
    if (!selectedTile) return;
    const qty = parseInt(selectedTile.dataset.qty, 10) || 1;
    const discount = parseInt(selectedTile.dataset.discount, 10) || 0;
    applyTier(root, qty, discount, parseInt(v.price, 10) || 0);
  });
})();

/* ============================================================
 * V9 — Cross-Sell-Box (Hoodie ↔ T-Shirt)
 * Checkbox + Variant-Selects → bei Add-to-Cart-Click wird das Partner-Produkt
 * automatisch zum Cart hinzugefügt mit Discount-Code-Property.
 * Lat/Lon vom Hauptprodukt werden auf das Partner-Produkt übernommen.
 * ============================================================ */
(function () {
  'use strict';

  function getMainState() {
    const mbRoot = document.querySelector('[data-ka-mapbox-block]');
    if (!mbRoot) return null;
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    return {
      lat: (form.querySelector('input[name="properties[_lat]"]') || {}).value || '',
      lon: (form.querySelector('input[name="properties[_lon]"]') || {}).value || '',
      koordinaten: (form.querySelector('input[name="properties[_Koordinaten]"]') || {}).value || '',
      ortsname: (form.querySelector('input[name="properties[_Ortsname]"]') || {}).value || '',
      adresse: (form.querySelector('input[name="properties[_Adresse]"]') || {}).value || '',
    };
  }

  function findPartnerVariantId(root, variantsData) {
    const partnerHandle = root.dataset.partnerHandle;
    const optionSelects = root.querySelectorAll('[data-ka-crosssell-option]');
    if (!optionSelects.length) return parseInt(root.dataset.defaultVariantId, 10);

    const selectedByPosition = {};
    optionSelects.forEach((sel) => {
      selectedByPosition[parseInt(sel.dataset.kaCrosssellOption, 10)] = sel.value;
    });

    const match = variantsData.find((variant) => {
      return variant.options.every((optValue, idx) => {
        const position = idx + 1;
        if (!(position in selectedByPosition)) return true;
        return selectedByPosition[position] === optValue;
      });
    });
    return match ? match.id : parseInt(root.dataset.defaultVariantId, 10);
  }

  function updatePriceDisplay(root, variantsData) {
    const variantId = findPartnerVariantId(root, variantsData);
    const variant = variantsData.find((v) => v.id === variantId);
    if (!variant) return;
    const discountPercent = parseInt(root.dataset.discountPercent, 10) || 10;
    const discountedCents = Math.round(variant.price * (100 - discountPercent) / 100);
    const priceEl = root.querySelector('[data-ka-crosssell-price]');
    const origEl = root.querySelector('[data-ka-crosssell-price-orig]');
    if (priceEl) priceEl.textContent = formatMoney(discountedCents);
    if (origEl) origEl.textContent = formatMoney(variant.price);
    const imgEl = root.querySelector('[data-ka-crosssell-img]');
    if (imgEl && variant.featured_image) {
      imgEl.src = variant.featured_image.src.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_200x.$1$2');
    }
  }

  function formatMoney(cents) {
    const eur = (cents / 100).toFixed(2).replace('.', ',');
    return eur + ' €';
  }

  function getVariantsForRoot(root) {
    const partnerHandle = root.dataset.partnerHandle;
    const scripts = document.querySelectorAll('script[id^="ka-crosssell-variants-"]');
    for (const sc of scripts) {
      try {
        const data = JSON.parse(sc.textContent);
        if (Array.isArray(data) && data.length > 0) return data;
      } catch (e) {}
    }
    return [];
  }

  function attachCartHijack(root, variantsData) {
    const buyForm = document.querySelector('form[action*="/cart/add"]');
    if (!buyForm) return;

    if (buyForm.dataset.kaCrosssellHooked === '1') return;
    buyForm.dataset.kaCrosssellHooked = '1';

    buyForm.addEventListener('submit', async (e) => {
      const checkbox = root.querySelector('[data-ka-crosssell-checkbox]');
      if (!checkbox || !checkbox.checked) return;
      e.preventDefault();

      const partnerVariantId = findPartnerVariantId(root, variantsData);
      const mainState = getMainState();
      const discountPercent = parseInt(root.dataset.discountPercent, 10) || 10;

      const properties = {};
      if (mainState.lat) properties['_lat'] = mainState.lat;
      if (mainState.lon) properties['_lon'] = mainState.lon;
      if (mainState.koordinaten) properties['_Koordinaten'] = mainState.koordinaten;
      if (mainState.ortsname) properties['_Ortsname'] = mainState.ortsname;
      if (mainState.adresse) properties['_Adresse'] = mainState.adresse;
      properties['_outfit_komplett'] = `${discountPercent}% Rabatt`;

      try {
        const mainFormData = new FormData(buyForm);
        const items = [{ id: parseInt(mainFormData.get('id'), 10), quantity: parseInt(mainFormData.get('quantity') || '1', 10) }];

        // Cart-Properties vom Hauptprodukt sind in mainFormData unter "properties[_X]"
        // Add Crossell als zweites Item via /cart/add.js (multi-item bulk):
        items.push({ id: partnerVariantId, quantity: 1, properties: properties });

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items.slice(0, 1) }) // erst Hauptprodukt (mit allen Properties aus mainFormData)
        });
        // dann Partner-Produkt
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items.slice(1) })
        });

        // V9.97.7: Free Gift Item als 3. POST hinzufügen falls aktiviert
        const freeGiftEl = root.querySelector('.ka-crosssell__freegift');
        const freeGiftId = freeGiftEl ? parseInt(freeGiftEl.dataset.kaFreegiftVariantId, 10) : null;
        if (freeGiftId) {
          const giftProperties = {
            '_is_free_gift': 'true',
            '_free_gift_for_variant': String(partnerVariantId)
          };
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id: freeGiftId, quantity: 1, properties: giftProperties }] })
            });
          } catch (giftErr) {
            // Gift-Add darf den Main-Flow nicht brechen — silent fail
            console.warn('Free gift cart-add failed', giftErr);
          }
        }

        // Cart-Drawer öffnen oder zur Cart-Page navigieren
        if (window.theme && typeof window.theme.openCart === 'function') {
          window.theme.openCart();
        } else {
          window.location.href = '/cart';
        }
      } catch (err) {
        console.error('Cross-sell cart-add failed', err);
        buyForm.submit();
      }
    }, true);
  }

  function init() {
    const root = document.querySelector('[data-ka-crosssell]');
    if (!root) return;
    const variantsData = getVariantsForRoot(root);
    if (!variantsData.length) return;

    updatePriceDisplay(root, variantsData);

    root.querySelectorAll('[data-ka-crosssell-option]').forEach((sel) => {
      sel.addEventListener('change', () => updatePriceDisplay(root, variantsData));
    });

    attachCartHijack(root, variantsData);
  }

  /* V9.97.7-fix1: Free-Gift Standalone-Hook — fügt Gift auch dann hinzu wenn
   * Cross-Sell-Checkbox NICHT checked ist (= bei normalem ATC). Skippt wenn
   * Cross-Sell-Hook bereits aktiv (Cross-Sell-Hijack fügt das Gift bereits hinzu). */
  function attachFreeGiftStandaloneHook() {
    const giftEl = document.querySelector('.ka-crosssell__freegift');
    if (!giftEl) return;
    const giftId = parseInt(giftEl.dataset.kaFreegiftVariantId, 10);
    if (!giftId) return;

    const buyForm = document.querySelector('form[action*="/cart/add"]');
    if (!buyForm || buyForm.dataset.kaFreegiftHooked === '1') return;
    buyForm.dataset.kaFreegiftHooked = '1';

    buyForm.addEventListener('submit', async (e) => {
      // Wenn Cross-Sell-Checkbox checked: Cross-Sell-Hook übernimmt + fügt Gift mit hinzu → wir tun nichts
      const csCheckbox = document.querySelector('.ka-crosssell__checkbox');
      if (csCheckbox && csCheckbox.checked) return;

      e.preventDefault();
      const formData = new FormData(buyForm);
      const mainId = parseInt(formData.get('id'), 10);
      const mainQty = parseInt(formData.get('quantity') || '1', 10);
      const mainProps = {};
      for (const [k, v] of formData.entries()) {
        if (k.startsWith('properties[') && k.endsWith(']')) {
          mainProps[k.slice(11, -1)] = v;
        }
      }
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: mainId, quantity: mainQty, properties: mainProps }] })
        });
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: giftId, quantity: 1, properties: { '_is_free_gift': 'true' } }] })
        });
        if (window.theme && typeof window.theme.openCart === 'function') {
          window.theme.openCart();
        } else {
          window.location.href = '/cart';
        }
      } catch (err) {
        console.warn('Free-gift standalone cart-add failed', err);
        buyForm.submit();
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); attachFreeGiftStandaloneHook(); });
  } else {
    init();
    attachFreeGiftStandaloneHook();
  }
  document.addEventListener('variant:change', () => setTimeout(init, 100));
})();


/* === V9.20 APPAREL COLOR-PICKER → Standard variant-picker Bridge ===
   Tile-Click triggert nativen Radio per data-option-position + value-id/name Match.
   Theme.js:3395 lauscht via Event-Delegation auf input[data-option-position][form].
   Memory: project_apparel_session_2026_05_12.md V9.20.1-V9.20.3 */
(function setupColorPickerBridge() {
  document.addEventListener('click', function(e) {
    const tile = e.target.closest('[data-ka-cp-value]');
    if (!tile) return;
    e.preventDefault();
    const picker = tile.closest('[data-ka-color-picker]');
    if (!picker) return;
    const optionPosition = picker.getAttribute('data-option-position');
    const valueId = tile.getAttribute('data-ka-cp-value-id');
    const valueName = tile.getAttribute('data-ka-cp-value');

    // Suche Standard-Radio: erst per value=ID (modern), Fallback value=Name (älteres Shopify)
    let radio = document.querySelector(`input[data-option-position="${optionPosition}"][value="${CSS.escape(valueId)}"]`);
    if (!radio) {
      radio = document.querySelector(`input[data-option-position="${optionPosition}"][value="${CSS.escape(valueName)}"]`);
    }
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      radio.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // UI-Update: is-selected umschalten innerhalb Picker
    picker.querySelectorAll('.ka-color-picker__tile').forEach(t => {
      const sel = t === tile;
      t.classList.toggle('is-selected', sel);
      t.setAttribute('aria-pressed', sel ? 'true' : 'false');
    });
    // Heading-Name aktualisieren
    const nameSpan = picker.querySelector('[data-ka-cp-name]');
    if (nameSpan) nameSpan.textContent = valueName;
  });

  // Sync von variant:change → Tile-State (wenn User Picker via Standard wechselt)
  document.addEventListener('variant:change', function(e) {
    const v = e.detail && e.detail.variant;
    if (!v) return;
    document.querySelectorAll('[data-ka-color-picker]').forEach(picker => {
      const optionPosition = parseInt(picker.getAttribute('data-option-position'), 10);
      if (!optionPosition) return;
      const selectedValue = v['option' + optionPosition];
      if (!selectedValue) return;
      let foundTile = null;
      picker.querySelectorAll('.ka-color-picker__tile').forEach(t => {
        const tileVal = t.getAttribute('data-ka-cp-value');
        const sel = tileVal === selectedValue;
        t.classList.toggle('is-selected', sel);
        t.setAttribute('aria-pressed', sel ? 'true' : 'false');
        if (sel) foundTile = t;
      });
      const nameSpan = picker.querySelector('[data-ka-cp-name]');
      if (nameSpan && selectedValue) nameSpan.textContent = selectedValue;
    });
  });
})();

/* === V9.79 APPAREL MAPBOX — Collapsible „Karte öffnen"-Toggle ===
   Klick auf [data-ka-map-toggle] togglet hidden + aria-hidden auf [data-ka-map]
   und aria-expanded auf dem Button. Icon ▾ ↔ ▴.
   Beim Öffnen: window resize-Event dispatchen, damit mapbox-gl die Canvas
   richtig dimensioniert (sonst grey-tile bis Page-Resize). */
(function setupMapboxToggle() {
  // V9.94.3 Guard gegen doppelte Registrierung wenn das Script doppelt
  // geladen wird (apparel-mapbox-block UND apparel-buybox-block laden's beide)
  if (window.__KA_MAPBOX_TOGGLE_REGISTERED__) return;
  window.__KA_MAPBOX_TOGGLE_REGISTERED__ = true;

  document.addEventListener('click', function(e) {
    const btn = e.target.closest('[data-ka-map-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const block = btn.closest('[data-ka-mapbox-block]');
    if (!block) return;
    const map = block.querySelector('[data-ka-map]');
    if (!map) return;

    const isOpen = btn.getAttribute('aria-expanded') === 'true';
    const icon = btn.querySelector('.ka-mb__map-toggle-icon');
    const txt = btn.querySelector('.ka-mb__map-toggle-text');

    if (isOpen) {
      btn.setAttribute('aria-expanded', 'false');
      map.setAttribute('hidden', '');
      map.setAttribute('aria-hidden', 'true');
      if (icon) icon.textContent = '▾';
      if (txt) txt.textContent = block.dataset.collapsibleOpenText || 'Karte öffnen';
    } else {
      btn.setAttribute('aria-expanded', 'true');
      map.removeAttribute('hidden');
      map.setAttribute('aria-hidden', 'false');
      if (icon) icon.textContent = '▴';
      if (txt) txt.textContent = block.dataset.collapsibleCloseText || 'Karte schließen';
      setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
    }
  });
})();

/* === V9.97.8 BUNDLE-Block JS — Variant-Selection + Subtotal + Cart-Add === */
(function () {
  function formatMoneyDE(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }

  function uuid() {
    // simple UUID v4-ish — gut genug als Bundle-ID, nicht crypto-Sicherheit
    return 'bundle-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function findVariantByOptions(variants, optionValues) {
    // optionValues = { '1': 'Black', '2': 'M' } (position -> value)
    return variants.find(function (v) {
      for (var pos in optionValues) {
        var val = optionValues[pos];
        if (!val) return false;
        var idx = parseInt(pos, 10) - 1;
        var vOpt = v.options[idx];
        if (vOpt !== val) return false;
      }
      return v.available !== false;
    });
  }

  function initBundleItem(itemEl) {
    var script = itemEl.querySelector('script[data-bundle-variants]');
    if (!script) return;
    var variants;
    try { variants = JSON.parse(script.textContent); } catch (e) { return; }
    if (!variants || !variants.length) return;

    var selects = itemEl.querySelectorAll('select[data-bundle-option]');

    function readSelected() {
      var optionValues = {};
      selects.forEach(function (sel) {
        optionValues[sel.dataset.bundleOption] = sel.value || null;
      });
      return optionValues;
    }

    function updateItem() {
      var optionValues = readSelected();
      // Wenn ein Produkt nur 1 Option (z.B. nur Größe ohne Farbe) hat, gibt's nur 1 Select
      // — dann fehlt position 2. Variant matcht trotzdem weil findVariantByOptions
      // den fehlenden Slot ignoriert (er ist nicht in optionValues).
      var allFilled = Object.keys(optionValues).length > 0;
      for (var pos in optionValues) {
        if (!optionValues[pos]) { allFilled = false; break; }
      }

      if (!allFilled) {
        itemEl.dataset.bundleCurrentVariantId = '';
        itemEl.dataset.bundleCurrentPrice = '';
        itemEl.dataset.bundleReady = '0';
      } else {
        var variant = findVariantByOptions(variants, optionValues);
        if (variant) {
          itemEl.dataset.bundleCurrentVariantId = String(variant.id);
          itemEl.dataset.bundleCurrentPrice = String(variant.price);
          itemEl.dataset.bundleReady = '1';
        } else {
          itemEl.dataset.bundleCurrentVariantId = '';
          itemEl.dataset.bundleCurrentPrice = '';
          itemEl.dataset.bundleReady = '0';
        }
      }

      // Subtotal-Update + ATC-Status auf Bundle-Root
      var bundle = itemEl.closest('.ka-bundle');
      if (bundle) updateBundleSubtotal(bundle);
    }

    selects.forEach(function (sel) {
      sel.addEventListener('change', updateItem);
    });

    // Initial-Run damit beim Page-Load der ATC bereits state hat
    updateItem();
  }

  function updateBundleSubtotal(bundleEl) {
    var items = bundleEl.querySelectorAll('.ka-bundle__item');
    var discount = parseFloat(bundleEl.dataset.discountPercent) || 0;
    var subtotalCents = 0;
    var allReady = items.length >= 2;
    items.forEach(function (item) {
      if (item.dataset.bundleReady === '1' && item.dataset.bundleCurrentPrice) {
        subtotalCents += parseInt(item.dataset.bundleCurrentPrice, 10) || 0;
      } else {
        allReady = false;
      }
    });

    var bundleCents = Math.round(subtotalCents * (1 - discount / 100));

    var strike = bundleEl.querySelector('[data-ka-bundle-strike]');
    var bundlePriceEl = bundleEl.querySelector('[data-ka-bundle-price]');
    var atc = bundleEl.querySelector('[data-ka-bundle-atc]');

    if (allReady && subtotalCents > 0) {
      if (strike) strike.textContent = formatMoneyDE(subtotalCents);
      if (bundlePriceEl) bundlePriceEl.textContent = formatMoneyDE(bundleCents);
      if (atc) {
        atc.disabled = false;
        atc.style.background = '';  // CSS-class :not([disabled]) übernimmt
        atc.textContent = atc.dataset.atcLabelReady || 'Im Bundle kaufen';
      }
    } else {
      if (strike) strike.textContent = '';
      if (bundlePriceEl) bundlePriceEl.textContent = '';
      if (atc) {
        atc.disabled = true;
        atc.textContent = atc.dataset.atcLabelIncomplete || 'Bitte Farbe und Größe wählen';
      }
    }
  }

  function attachBundleAtc(bundleEl) {
    var atc = bundleEl.querySelector('[data-ka-bundle-atc]');
    if (!atc) return;
    atc.addEventListener('click', function (e) {
      e.preventDefault();
      if (atc.disabled) return;
      var items = bundleEl.querySelectorAll('.ka-bundle__item');
      var bundleId = uuid();
      var cartItems = [];
      items.forEach(function (item) {
        var vid = parseInt(item.dataset.bundleCurrentVariantId, 10);
        if (!vid) return;
        cartItems.push({
          id: vid,
          quantity: 1,
          properties: {
            '_bundle_id': bundleId,
            '_bundle_member': 'true'
          }
        });
      });
      if (cartItems.length < 2) return;

      var originalText = atc.textContent;
      atc.disabled = true;
      atc.textContent = 'Wird hinzugefügt...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      }).then(function (res) {
        if (!res.ok) throw new Error('Bundle add failed');
        return res.json().catch(function () { return null; });
      }).then(function (cartData) {
        // GTM dataLayer AddToCart Event
        if (cartData && window.DLTrack && window.DLTrack.addToCart) {
          window.DLTrack.addToCart(cartData);
        }
        if (window.theme && typeof window.theme.openCart === 'function') {
          window.theme.openCart();
        } else {
          window.location.href = '/cart';
        }
      }).catch(function (err) {
        console.error('Bundle cart-add failed', err);
        atc.disabled = false;
        atc.textContent = originalText;
      });
    });
  }

  function initBundle() {
    var bundles = document.querySelectorAll('[data-ka-bundle]');
    bundles.forEach(function (bundleEl) {
      var items = bundleEl.querySelectorAll('.ka-bundle__item');
      items.forEach(initBundleItem);
      attachBundleAtc(bundleEl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundle);
  } else {
    initBundle();
  }
})();
